import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Alert,
  Snackbar,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SecurityIcon from '@mui/icons-material/Security';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { Producto } from '../../services/productos';
import { useAuth } from '../../hooks/useAuth';
import { WHATSAPP_BUSINESS_NUMBER } from '../../config/whatsapp';
// import MercadoPagoButton from './MercadoPagoButton'; // COMENTADO: Solo pagos presenciales/transferencia
import { 
  getDirecciones,
  createDireccion,
  Direccion,
  DireccionFormData,
  validateDireccion,
} from '../../services/direcciones';
import {
  getMetodosPago,
  createMetodoPago,
  MetodoPago as MetodoPagoType,
} from '../../services/metodosPago';
import {
  createPedido,
  CrearPedidoData,
  MetodoPago,
} from '../../services/pedidos';
import { Region, Comuna, getRegiones, getComunas, getComunasByRegion as getComunasByRegionAPI } from '../../services/regiones';

// COMENTADO: Código de tarjetas eliminado - Solo pagos presenciales/transferencia por WhatsApp
// Tipos, funciones y componentes de marcas de tarjeta removidos

// Helper functions locales
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

const getProductImage = (producto: Producto): string => {
  return producto.imagen || '/assets/producto-placeholder.png';
};

const CheckoutContainer = styled(Container)({
  minHeight: '100vh',
  paddingTop: '40px',
  paddingBottom: '40px',
});

const StepCard = styled(Card)({
  borderRadius: '20px',
  boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
  border: '1px solid rgba(46, 90, 51, 0.1)',
  marginBottom: '30px',
});

const SummaryCard = styled(Card)({
  borderRadius: '20px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
  border: '1px solid rgba(46, 90, 51, 0.1)',
  position: 'sticky',
  top: '20px',
});

const MercadoPagoOption = styled(Box)({
  border: '2px solid #00a8ff',
  borderRadius: '15px',
  padding: '20px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 25px rgba(0, 168, 255, 0.2)',
    transform: 'translateY(-2px)',
  },
});

// Estilos comunes para los TextFields
const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: { xs: '12px', sm: '14px' },
    backgroundColor: '#ffffff',
    fontSize: { xs: '0.875rem', sm: '1rem' },
    border: '1.5px solid #e8e8e8',
    transition: 'all 0.3s ease',
    '& fieldset': {
      border: 'none',
    },
    '&:hover': {
      backgroundColor: '#ffffff',
      border: '1.5px solid #2e5a33',
      boxShadow: '0 2px 8px rgba(46, 90, 51, 0.1)',
      transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      border: '2px solid #2e5a33',
      boxShadow: '0 4px 12px rgba(46, 90, 51, 0.15)',
    },
    '& input::placeholder': {
      color: '#aaa',
      opacity: 1,
    },
    '& textarea::placeholder': {
      color: '#aaa',
      opacity: 1,
    }
  },
  '& .MuiInputLabel-root': {
    fontSize: { xs: '0.875rem', sm: '1rem' },
    fontWeight: 500,
    color: '#555',
    backgroundColor: 'transparent',
    '&.Mui-focused': {
      color: '#2e5a33',
      fontWeight: 600,
    },
    '&.MuiInputLabel-shrink': {
      backgroundColor: '#fafbfc',
      padding: '0 8px',
    }
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '8px',
    fontSize: '0.75rem',
    color: '#777',
  }
};

const steps = [
  'Revisar Pedido',
  'Dirección de Envío',
  'Método de Pago',
  'Confirmar Compra'
];

const emptyDireccion: DireccionFormData = {
  apodo: '',
  calle: '',
  numero: '',
  comuna: '',
  ciudad: '',
  region: '',
  codigo_postal: '',
  referencia: '',
  telefono: '',
  nombre_contacto: '',
  es_principal: false,
};

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { items, getTotalItems, getTotalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Debug logs
  console.log('🛒 Checkout - Items en carrito:', items.length);
  console.log('👤 Checkout - Usuario autenticado:', !!user);
  console.log('📦 Checkout - Items:', items);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedAddress, setSelectedAddress] = useState<number | null>(null);
  const [selectedPayment, setSelectedPayment] = useState('mercadopago');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'whatsapp' | 'online'>('online');
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [direcciones, setDirecciones] = useState<Direccion[]>([]);
  const [metodosPago, setMetodosPago] = useState<MetodoPagoType[]>([]);
  const [dialogDireccionOpen, setDialogDireccionOpen] = useState(false);
  const [formDireccion, setFormDireccion] = useState<DireccionFormData>(emptyDireccion);
  const [regiones, setRegiones] = useState<Region[]>([]);
  const [comunas, setComunas] = useState<Comuna[]>([]);
  const [comunasFiltradas, setComunasFiltradas] = useState<Comuna[]>([]);
  const [savingAddress, setSavingAddress] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // COMENTADO: Estados de tarjetas eliminados - Solo pagos por WhatsApp

  // Cargar regiones y comunas desde la BD
  useEffect(() => {
    const loadRegionesYComunas = async () => {
      try {
        const [regionesData, comunasData] = await Promise.all([
          getRegiones(),
          getComunas()
        ]);
        
        console.log('Regiones cargadas:', regionesData);
        console.log('Comunas cargadas:', comunasData);
        
        // Asegurarnos de que sean arrays
        setRegiones(Array.isArray(regionesData) ? regionesData : []);
        setComunas(Array.isArray(comunasData) ? comunasData : []);
      } catch (error) {
        console.error('Error al cargar regiones y comunas:', error);
        setRegiones([]);
        setComunas([]);
        setSnackbar({
          open: true,
          message: 'Error al cargar regiones y comunas',
          severity: 'error'
        });
      }
    };
    
    loadRegionesYComunas();
  }, []);

  // Cargar direcciones y métodos de pago del usuario
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Cargar direcciones desde Django
        const direccionesData = await getDirecciones();
        console.log('Direcciones cargadas:', direccionesData);
        
        // Asegurarnos de que sea un array
        const direccionesArray = Array.isArray(direccionesData) ? direccionesData : [];
        setDirecciones(direccionesArray);

        // Seleccionar la dirección predeterminada o la primera
        const defaultDir = direccionesArray.find(d => d.es_principal);
        if (defaultDir) {
          setSelectedAddress(defaultDir.id);
        } else if (direccionesArray.length > 0) {
          setSelectedAddress(direccionesArray[0].id);
        }

        // COMENTADO: Carga de métodos de pago eliminada - Solo WhatsApp

      } catch (error) {
        console.error('Error al cargar datos del usuario:', error);
        setDirecciones([]);
        setMetodosPago([]);
        setSnackbar({
          open: true,
          message: 'Error al cargar tus datos. Por favor, recarga la página.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user]);

  // Filtrar comunas según la región seleccionada
  useEffect(() => {
    if (formDireccion.region) {
      console.log('🔍 Filtrando comunas para región:', formDireccion.region);
      console.log('📋 Total regiones disponibles:', regiones.length);
      console.log('📋 Total comunas disponibles:', comunas.length);
      
      const regionSeleccionada = regiones.find(r => r.nombre === formDireccion.region);
      
      if (regionSeleccionada) {
        console.log('✅ Región encontrada:', regionSeleccionada);
        const comunasFiltradas = comunas.filter(c => c.region_id === regionSeleccionada.id);
        console.log('📍 Comunas filtradas:', comunasFiltradas.length, comunasFiltradas);
        setComunasFiltradas(comunasFiltradas);
      } else {
        console.warn('❌ No se encontró la región:', formDireccion.region);
        console.log('Regiones disponibles:', regiones.map(r => r.nombre));
        setComunasFiltradas([]);
      }
    } else {
      setComunasFiltradas([]);
    }
  }, [formDireccion.region, regiones, comunas]);

  // COMENTADO: Funciones de manejo de tarjetas eliminadas - Solo pagos por WhatsApp

  // ========== FUNCIONES PARA DIRECCIONES ==========
  const handleOpenDialogDireccion = () => {
    setFormDireccion(emptyDireccion);
    setDialogDireccionOpen(true);
  };

  const handleCloseDialogDireccion = () => {
    setDialogDireccionOpen(false);
    setFormDireccion(emptyDireccion);
    setComunasFiltradas([]);
  };

  const handleSaveDireccion = async () => {
    if (!user) return;

    // Preparar datos en el formato del servicio, limpiando espacios en blanco
    const addressData: DireccionFormData = {
      calle: (formDireccion.calle || '').trim(),
      numero: (formDireccion.numero || '').trim(),
      comuna: (formDireccion.comuna || '').trim(),
      ciudad: (formDireccion.ciudad || formDireccion.comuna || '').trim(),
      region: (formDireccion.region || '').trim(),
      codigo_postal: (formDireccion.codigo_postal || '').trim(),
      referencia: (formDireccion.referencia || '').trim(),
      telefono: (formDireccion.telefono || '').trim(),
      nombre_contacto: (formDireccion.nombre_contacto || user.first_name + ' ' + user.last_name || user.email || '').trim(),
      apodo: (formDireccion.apodo || '').trim(),
      es_principal: formDireccion.es_principal || false,
    };

    // Validar usando el servicio
    const { valid, errors } = validateDireccion(addressData);
    if (!valid) {
      const errorMessages = Object.values(errors).join(', ');
      setSnackbar({
        open: true,
        message: `Por favor completa correctamente: ${errorMessages}`,
        severity: 'error'
      });
      return;
    }

    try {
      setSavingAddress(true);

      console.log('📤 Enviando datos de dirección:', addressData);

      // Usar el servicio de Django para guardar
      const newAddress = await createDireccion(addressData);

      // Recargar direcciones
      const direccionesData = await getDirecciones();
      setDirecciones(direccionesData);

      // Seleccionar la nueva dirección automáticamente
      setSelectedAddress(newAddress.id);

      setSnackbar({
        open: true,
        message: '✅ Dirección guardada exitosamente',
        severity: 'success'
      });

      handleCloseDialogDireccion();
    } catch (error: any) {
      console.error('Error al guardar dirección:', error);
      
      // Extraer mensaje de error específico del backend
      let errorMessage = 'Error al guardar la dirección. Intenta nuevamente.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        // Si es un objeto con campos, mostrar el primer error
        if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          const firstError = Object.values(errorData)[0];
          if (Array.isArray(firstError)) {
            errorMessage = firstError[0];
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSavingAddress(false);
    }
  };

  // Redirigir si no hay productos en el carrito
  if (items.length === 0) {
    return (
      <CheckoutContainer maxWidth="md">
        <Card sx={{ textAlign: 'center', p: 4, borderRadius: '20px' }}>
          <ShoppingCartIcon sx={{ fontSize: 80, color: '#e0e0e0', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Tu carrito está vacío
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Agrega algunos productos antes de proceder al checkout
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/productos')}
            sx={{
              background: 'linear-gradient(45deg, #2e5a33, #4a7c59)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
              },
            }}
          >
            Ir a Productos
          </Button>
        </Card>
      </CheckoutContainer>
    );
  }

  // Verificar autenticación
  if (!user && !loading) {
    return (
      <CheckoutContainer maxWidth="md">
        <Card sx={{ textAlign: 'center', p: 4, borderRadius: '20px' }}>
          <Typography variant="h5" gutterBottom>
            Debes iniciar sesión
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Inicia sesión para continuar con tu compra
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            sx={{
              background: 'linear-gradient(45deg, #2e5a33, #4a7c59)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
              },
            }}
          >
            Iniciar Sesión
          </Button>
        </Card>
      </CheckoutContainer>
    );
  }

  // Mostrar loading mientras se cargan los datos
  if (loading) {
    return (
      <CheckoutContainer maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress sx={{ color: '#2e5a33' }} />
        </Box>
      </CheckoutContainer>
    );
  }

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleCompletePurchase();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // COMENTADO: Funciones de pago con tarjeta - ahora solo WhatsApp con pago presencial/transferencia
  /*
  const createPedidoForPaymentGateway = async (paymentType: 'mercadopago' | 'webpay' | 'khipu'): Promise<string | number> => {
    if (!selectedAddress) {
      throw new Error('Por favor selecciona una dirección de envío');
    }
    const itemsData = items.map(item => ({
      producto: item.product.id,
      cantidad: item.quantity,
      precio_unitario: parseFloat(item.product.precio_venta.toString())
    }));
    const pedidoData: CrearPedidoData = {
      direccion_id: selectedAddress,
      metodo_pago: paymentType,
      notas_cliente: '',
      items: itemsData,
    };
    const pedido = await createPedido(pedidoData);
    return pedido.id;
  };
  const createPedidoForMercadoPago = async (): Promise<string | number> => {
    return createPedidoForPaymentGateway('mercadopago');
  };
  */

  const handleCompletePurchase = async () => {
    // SOLO WhatsApp - redirigir directamente
    handleWhatsAppOrder();
    return;

    // COMENTADO: Código de validación de métodos de pago eliminado - Solo WhatsApp
    /*
    if (paymentMethod === 'whatsapp') {
      handleWhatsAppOrder();
      return;
    }

    // Validar que se haya seleccionado dirección y método de pago
    if (!selectedAddress) {
      setSnackbar({
        open: true,
        message: 'Por favor selecciona una dirección de envío',
        severity: 'warning'
      });
      return;
    }

    if (!selectedPaymentMethod) {
      setSnackbar({
        open: true,
        message: 'Por favor selecciona un método de pago',
        severity: 'warning'
      });
      return;
    }

    // Obtener el método de pago seleccionado
    const metodoPagoSeleccionado = metodosPago.find(m => m.id === selectedPaymentMethod);
    
    if (!metodoPagoSeleccionado) {
      setSnackbar({
        open: true,
        message: 'Método de pago no encontrado',
        severity: 'error'
      });
      return;
    }

    setProcessing(true);

    try {
      // Preparar items para el pedido
      const itemsData = items.map(item => ({
        producto: item.product.id,
        cantidad: item.quantity,
        precio_unitario: parseFloat(item.product.precio_venta.toString())
      }));

      // Determinar el método de pago correcto según el tipo
      let metodoPagoString: string = 'efectivo';
      const tipoMetodo = metodoPagoSeleccionado.tipo;
      
      if (tipoMetodo === 'tarjeta_credito' || tipoMetodo === 'tarjeta_debito') {
        // Las tarjetas son solo de referencia - informar al usuario
        setProcessing(false);
        setSnackbar({
          open: true,
          message: '⚠️ Las tarjetas guardadas son solo de referencia. Por favor usa el botón de MercadoPago para procesar el pago.',
          severity: 'warning'
        });
        setActiveStep(2); // Volver al paso de pago
        return;
      } else if (tipoMetodo === 'mercado_pago') {
        metodoPagoString = 'mercadopago';
      } else if (tipoMetodo === 'webpay') {
        metodoPagoString = 'webpay';
      } else if (tipoMetodo === 'google_pay' || tipoMetodo === 'khipu') {
        metodoPagoString = 'digital';
      }

      // Crear datos del pedido con el método de pago correcto
      const pedidoData: CrearPedidoData = {
        items: itemsData,
        direccion_id: selectedAddress,
        metodo_pago: metodoPagoString as any,
        notas_cliente: '',
      };

      console.log('📝 Creando pedido con datos completos:', {
        metodo_pago: metodoPagoString,
        direccion_id: selectedAddress,
        items_count: itemsData.length,
        pedidoData
      });

      // Crear el pedido en Django
      const pedido = await createPedido(pedidoData);

      // Limpiar carrito
      clearCart();

      setSnackbar({
        open: true,
        message: `¡Compra realizada exitosamente! Pedido #${pedido.numero_pedido}`,
        severity: 'success'
      });

      // Redirigir a página de éxito
      setTimeout(() => {
        navigate(`/checkout/success/${pedido.id}`);
      }, 2000);

    } catch (error: any) {
      console.error('❌ Error al procesar la compra:', error);
      console.error('Response data:', error?.response?.data);
      
      // Extraer mensaje de error más específico
      let errorMessage = 'Error al procesar la compra. Intenta nuevamente.';
      
      if (error?.response?.data) {
        // Si hay un objeto de errores (validación de Django)
        if (typeof error.response.data === 'object') {
          const errores = Object.entries(error.response.data)
            .map(([campo, mensajes]: [string, any]) => {
              if (Array.isArray(mensajes)) {
                return `${campo}: ${mensajes.join(', ')}`;
              }
              return `${campo}: ${mensajes}`;
            })
            .join(' | ');
          errorMessage = errores || error?.response?.data?.error || errorMessage;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setProcessing(false);
    }
    */
  };

  const handleWhatsAppOrder = async () => {
    // Validar que haya dirección seleccionada
    if (!selectedAddress) {
      setSnackbar({
        open: true,
        message: 'Por favor selecciona una dirección de envío',
        severity: 'warning'
      });
      return;
    }

    setProcessing(true);

    try {
      // Primero crear el pedido en la base de datos
      const itemsData = items.map(item => ({
        producto: item.product.id,
        cantidad: item.quantity,
        precio_unitario: parseFloat(item.product.precio_venta.toString())
      }));

      const pedidoData: CrearPedidoData = {
        items: itemsData,
        direccion_id: selectedAddress,
        metodo_pago: 'whatsapp' as any, // Método de pago WhatsApp
        notas_cliente: 'Pedido realizado por WhatsApp - Pago pendiente de confirmar',
      };

      console.log('📝 Creando pedido WhatsApp:', pedidoData);

      // Crear el pedido en Django
      const pedido = await createPedido(pedidoData);

      console.log('✅ Pedido creado:', pedido);

      // Ahora construir el mensaje de WhatsApp con el número de pedido
      const selectedAddr = direcciones.find(addr => addr.id === selectedAddress);

      let message = `¡Hola! 👋 Quisiera confirmar mi pedido:\n\n`;
      message += `*PEDIDO #${pedido.numero_pedido}*\n\n`;
      message += `*PRODUCTOS:*\n`;

      items.forEach((item, index) => {
        message += `${index + 1}. ${item.product.nombre}\n`;
        message += `   Cantidad: ${item.quantity}\n`;
        message += `   Precio: ${formatPrice(item.product.precio_venta)} c/u\n`;
        message += `   Subtotal: ${formatPrice(Number(item.product.precio_venta) * item.quantity)}\n\n`;
      });

      message += `*DIRECCIÓN DE ENVÍO:*\n`;
      message += `📍 ${selectedAddr?.apodo || 'Dirección'}\n`;
      message += `📍 ${selectedAddr?.calle} ${selectedAddr?.numero}\n`;
      message += `📍 ${selectedAddr?.comuna}, ${selectedAddr?.region}\n`;
      message += `📞 ${selectedAddr?.telefono}\n`;
      if (selectedAddr?.referencia) {
        message += `📝 ${selectedAddr.referencia}\n`;
      }
      message += `\n`;

      message += `*TOTAL A PAGAR:* ${formatPrice(getTotalPrice())}\n\n`;
      message += `Usuario: ${user?.email || 'Cliente'}\n`;
      message += `Fecha: ${new Date().toLocaleDateString('es-CL')}\n\n`;
      message += `¿Cómo desean realizar el pago? (Efectivo al recibir o transferencia)`;

      // Codificar el mensaje para URL
      const encodedMessage = encodeURIComponent(message);

      // Crear el enlace de WhatsApp
      const whatsappUrl = `https://wa.me/${WHATSAPP_BUSINESS_NUMBER}?text=${encodedMessage}`;

      console.log('📱 Redirigiendo a WhatsApp:', whatsappUrl);

      // Mostrar mensaje de confirmación primero
      setSnackbar({
        open: true,
        message: `✅ Pedido #${pedido.numero_pedido} creado. Te redirigimos a WhatsApp 📱`,
        severity: 'success'
      });

      // Intentar múltiples métodos de redirección para máxima compatibilidad
      try {
        // Método 1: Usar window.location.href (más confiable en móviles)
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
          // En móviles, usar location.href directamente
          window.location.href = whatsappUrl;
        } else {
          // En desktop, intentar abrir en nueva pestaña
          const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
          
          // Si el popup fue bloqueado, usar location.href como fallback
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            console.warn('⚠️ Popup bloqueado, usando redirección directa');
            window.location.href = whatsappUrl;
          }
        }
      } catch (error) {
        console.error('❌ Error al abrir WhatsApp:', error);
        // Fallback final: usar location.href
        window.location.href = whatsappUrl;
      }

      // Limpiar el carrito después de enviar a WhatsApp (con delay mayor)
      setTimeout(() => {
        clearCart();
        // No redirigir al home automáticamente para dar tiempo a WhatsApp
      }, 3000);

    } catch (error: any) {
      console.error('❌ Error al crear pedido WhatsApp:', error);
      
      let errorMessage = 'Error al crear el pedido. Intenta nuevamente.';
      
      if (error?.response?.data) {
        if (typeof error.response.data === 'object') {
          const errores = Object.entries(error.response.data)
            .map(([campo, mensajes]: [string, any]) => {
              if (Array.isArray(mensajes)) {
                return `${campo}: ${mensajes.join(', ')}`;
              }
              return `${campo}: ${mensajes}`;
            })
            .join(' | ');
          errorMessage = errores || errorMessage;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setProcessing(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Revisar Productos ({getTotalItems()} artículo{getTotalItems() !== 1 ? 's' : ''})
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
              <Typography variant="body2">
                Los productos en esta lista no pueden ser modificados durante el proceso de compra. 
                Si deseas hacer cambios, <Button 
                  size="small" 
                  onClick={() => navigate('/productos')}
                  sx={{ 
                    textTransform: 'none', 
                    p: 0, 
                    minWidth: 'auto',
                    color: '#2e5a33',
                    fontWeight: 'bold',
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                  }}
                >
                  vuelve a la tienda
                </Button> antes de continuar.
              </Typography>
            </Alert>
            
            <List>
              {items.map((item, index) => (
                <React.Fragment key={`cart-item-${item.product.id}-${index}`}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar
                        src={getProductImage(item.product)}
                        alt={item.product.nombre}
                        sx={{ width: 60, height: 60, borderRadius: '12px' }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.product.nombre}
                      secondary={`Cantidad: ${item.quantity} - ${formatPrice(item.product.precio_venta)} c/u`}
                      sx={{ ml: 2 }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e5a33' }}>
                      {formatPrice(Number(item.product.precio_venta) * item.quantity)}
                    </Typography>
                  </ListItem>
                  {index < items.length - 1 && <Divider key={`divider-${index}`} />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Seleccionar Dirección de Envío
            </Typography>

            {direcciones.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '15px' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  No tienes direcciones guardadas
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleOpenDialogDireccion}
                  startIcon={<AddIcon />}
                  sx={{
                    borderColor: '#2e5a33',
                    color: '#2e5a33',
                    '&:hover': {
                      borderColor: '#1a3d1e',
                      bgcolor: 'rgba(46, 90, 51, 0.04)',
                    },
                  }}
                >
                  Agregar Dirección
                </Button>
              </Alert>
            ) : (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={selectedAddress}
                  onChange={(e) => setSelectedAddress(parseInt(e.target.value))}
                >
                  {direcciones.map((address) => (
                    <Card
                      key={address.id}
                      sx={{
                        mb: 2,
                        border: selectedAddress === address.id ? '2px solid #2e5a33' : '1px solid #e0e0e0',
                        borderRadius: '15px',
                      }}
                    >
                      <CardContent>
                        <FormControlLabel
                          value={address.id}
                          control={<Radio sx={{ color: '#2e5a33' }} />}
                          label={
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                  {address.apodo || 'Sin nombre'}
                                </Typography>
                                {address.es_principal && (
                                  <Chip
                                    label="Predeterminada"
                                    size="small"
                                    sx={{ bgcolor: '#2e5a33', color: 'white' }}
                                  />
                                )}
                              </Box>
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                {address.calle} {address.numero}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {address.comuna}, {address.region} - {address.telefono}
                              </Typography>
                              {address.referencia && (
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                  📝 {address.referencia}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  ))}
                </RadioGroup>

                {/* Botón para agregar otra dirección */}
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenDialogDireccion}
                  startIcon={<AddIcon />}
                  sx={{
                    mt: 2,
                    borderColor: '#2e5a33',
                    color: '#2e5a33',
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    py: 1.5,
                    '&:hover': {
                      borderColor: '#1a3d1e',
                      bgcolor: 'rgba(46, 90, 51, 0.04)',
                      borderStyle: 'dashed',
                      borderWidth: 2,
                    },
                  }}
                >
                  Agregar otra dirección
                </Button>
              </FormControl>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              Realizar Pedido por WhatsApp
            </Typography>

            {/* Solo pedidos por WhatsApp */}
            <Card
              sx={{
                border: '3px solid #25D366',
                borderRadius: '15px',
                mb: 3
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Avatar
                  sx={{
                    width: 70,
                    height: 70,
                    bgcolor: '#25D366',
                    margin: '0 auto 16px',
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Pedir por WhatsApp
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Envía tu pedido y coordina el pago (efectivo o transferencia) con nosotros
                </Typography>
                <Chip
                  label="Pago al recibir o por transferencia"
                  size="small"
                  sx={{
                    bgcolor: '#25D366',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
              </CardContent>
            </Card>

            {/* COMENTADO: Sección de métodos de pago eliminada - Solo WhatsApp */}

            <Alert severity="info" sx={{ mb: 2 }}>
              📱 <strong>Pedidos solo por WhatsApp</strong> - Pago presencial o por transferencia. Confirma tu pedido y coordinaremos la entrega contigo.
            </Alert>

            {/* Información de seguridad */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 3,
              p: 2,
              bgcolor: '#f5f5f5',
              borderRadius: 2
            }}>
              <SecurityIcon sx={{ color: '#2e5a33' }} />
              <Typography variant="caption" color="textSecondary">
                Todos los pagos son 100% seguros. Tus datos están protegidos.
              </Typography>
            </Box>
          </Box>
        );

      case 3:
        const selectedAddr = direcciones.find(addr => addr.id === selectedAddress);
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              Confirmar {paymentMethod === 'whatsapp' ? 'Pedido' : 'Compra'}
            </Typography>

            {paymentMethod === 'whatsapp' ? (
              <Alert severity="info" sx={{ mb: 3, borderRadius: '15px' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  📱 Pedido por WhatsApp
                </Typography>
                <Typography variant="body2">
                  Al confirmar, serás redirigido a WhatsApp con los detalles de tu pedido.
                  Podrás coordinar el pago y entrega directamente con nosotros.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 3, borderRadius: '15px' }}>
                Revisa todos los datos antes de confirmar tu compra
              </Alert>
            )}

            <Card sx={{ mb: 3, borderRadius: '15px' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  📦 Productos ({getTotalItems()})
                </Typography>
                {items.slice(0, 3).map((item, idx) => (
                  <Typography key={`summary-item-${item.product.id}-${idx}`} variant="body2" color="textSecondary">
                    • {item.product.nombre} x{item.quantity}
                  </Typography>
                ))}
                {items.length > 3 && (
                  <Typography variant="body2" color="textSecondary">
                    ... y {items.length - 3} producto{items.length - 3 !== 1 ? 's' : ''} más
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mb: 3, borderRadius: '15px' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  🏠 Dirección de Envío
                </Typography>
                {selectedAddr ? (
                  <>
                    <Typography variant="body2">{selectedAddr.apodo || 'Dirección'}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedAddr.calle} {selectedAddr.numero}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedAddr.comuna}, {selectedAddr.region}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedAddr.telefono}
                    </Typography>
                    {selectedAddr.referencia && (
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        📝 {selectedAddr.referencia}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="error">
                    No se ha seleccionado una dirección
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: '15px' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  💬 Método de Contacto
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ bgcolor: '#25D366', width: 24, height: 24 }}>
                    <WhatsAppIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="body2">WhatsApp - Pago al recibir o transferencia</Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <CheckoutContainer maxWidth="lg">
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold', textAlign: 'center', color: '#2e5a33' }}>
        Finalizar Compra
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
        <Box sx={{ width: { xs: '100%', md: '66.6667%' } }}>
          <StepCard>
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Stepper
                activeStep={activeStep}
                orientation={isMobile ? 'vertical' : 'horizontal'}
                sx={{
                  mb: 4,
                  '& .MuiStepLabel-root': {
                    padding: { xs: '0 4px', sm: '0 8px' }
                  },
                  '& .MuiStepConnector-line': {
                    minHeight: { xs: '20px', sm: '24px' }
                  }
                }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel
                      sx={{
                        '& .MuiStepLabel-label': {
                          fontSize: { xs: '0.875rem', sm: '0.875rem' },
                          marginTop: { xs: '4px !important', sm: '8px !important' }
                        },
                        '& .MuiStepLabel-label.Mui-active': {
                          color: '#2e5a33',
                          fontWeight: 'bold',
                        },
                        '& .MuiStepLabel-label.Mui-completed': {
                          color: '#2e5a33',
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box key={`step-content-${activeStep}`}>
                {renderStepContent(activeStep)}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ color: '#666' }}
                >
                  Atrás
                </Button>
                
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={processing}
                  startIcon={
                    activeStep === steps.length - 1 && <WhatsAppIcon />
                  }
                  sx={{
                    background: activeStep === steps.length - 1 ?
                      'linear-gradient(45deg, #25D366, #128C7E)' :
                      'linear-gradient(45deg, #2e5a33, #4a7c59)',
                    '&:hover': {
                      background: activeStep === steps.length - 1 ?
                        'linear-gradient(45deg, #128C7E, #075E54)' :
                        'linear-gradient(45deg, #1a3d1e, #2e5a33)',
                    },
                  }}
                >
                    {processing ? 'Procesando...' :
                      activeStep === steps.length - 1 ? 'Confirmar Pedido por WhatsApp' :
                      'Continuar'}
                </Button>
              </Box>
            </CardContent>
          </StepCard>

        </Box>

        <Box sx={{ width: { xs: '100%', md: '33.3333%' } }}>
          <SummaryCard sx={{ p: 4, position: { xs: 'static', md: 'sticky' }, top: { md: '20px' } }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                Resumen del Pedido
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Productos ({getTotalItems()})</Typography>
                  <Typography variant="body2">{formatPrice(getTotalPrice())}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Envío</Typography>
                  <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                    Gratis
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e5a33' }}>
                    {formatPrice(getTotalPrice())}
                  </Typography>
                </Box>
              </Box>

              <Alert severity="success" sx={{ borderRadius: '15px' }}>
                <Typography variant="body2">
                  🚚 <strong>Envío gratis</strong> en compras sobre $15.000
                </Typography>
              </Alert>
            </CardContent>
          </SummaryCard>
        </Box>

      </Box>

      {/* Diálogo para agregar dirección */}
      <Dialog
        open={dialogDireccionOpen}
        onClose={handleCloseDialogDireccion}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 6 },
            maxWidth: { xs: 'calc(100% - 32px)', sm: '900px' },
            borderRadius: { xs: '16px', sm: '20px' },
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          pt: 3,
          px: { xs: 2, sm: 6 },
          background: 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%)',
          color: 'white',
          borderTopLeftRadius: { xs: '16px', sm: '20px' },
          borderTopRightRadius: { xs: '16px', sm: '20px' },
        }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
          >
            Agregar Nueva Dirección
          </Typography>
        </DialogTitle>
        <DialogContent sx={{
          pt: 4,
          pb: 3,
          px: { xs: 2.5, sm: 3.5 },
          backgroundColor: '#fafbfc'
        }}>
          <Grid container spacing={3} sx={{ mt: 5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nombre de la dirección *"
                value={formDireccion.apodo}
                onChange={(e) => setFormDireccion({ ...formDireccion, apodo: e.target.value })}
                placeholder="Ej: Mi Casa, Oficina"
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nombre de contacto"
                value={formDireccion.nombre_contacto}
                onChange={(e) => setFormDireccion({ ...formDireccion, nombre_contacto: e.target.value })}
                placeholder="Ej: Juan Pérez"
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Calle *"
                value={formDireccion.calle}
                onChange={(e) => setFormDireccion({ ...formDireccion, calle: e.target.value })}
                placeholder="Av. Las Condes"
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Número *"
                value={formDireccion.numero}
                onChange={(e) => {
                  const value = e.target.value;
                  // Solo permitir números y guiones
                  if (/^[0-9\-]*$/.test(value)) {
                    setFormDireccion({ ...formDireccion, numero: value });
                  }
                }}
                placeholder="1234"
                inputProps={{ maxLength: 10 }}
                helperText="Solo números (ej: 1234 o 123-A)"
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="Región *"
                value={formDireccion.region}
                onChange={(e) => {
                  setFormDireccion({ ...formDireccion, region: e.target.value, comuna: '' });
                }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              >
                <option value="">Seleccione una región</option>
                {regiones.map((region) => (
                  <option key={region.id} value={region.nombre}>
                    {region.nombre}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="Comuna *"
                value={formDireccion.comuna}
                onChange={(e) => setFormDireccion({ ...formDireccion, comuna: e.target.value })}
                SelectProps={{ 
                  native: true,
                  sx: {
                    '& option': {
                      padding: '8px 12px',
                      fontSize: '0.95rem',
                    }
                  }
                }}
                InputLabelProps={{ shrink: true }}
                disabled={!formDireccion.region}
                helperText={!formDireccion.region ? 'Primero selecciona una región' : `${comunasFiltradas.length} comunas disponibles`}
                sx={{
                  ...textFieldStyles,
                  '& select': {
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }
                }}
              >
                <option value="">Seleccione una comuna</option>
                {comunasFiltradas.map((comuna) => (
                  <option key={comuna.id} value={comuna.nombre}>
                    {comuna.nombre}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Ciudad *"
                value={formDireccion.ciudad}
                onChange={(e) => setFormDireccion({ ...formDireccion, ciudad: e.target.value })}
                placeholder="Ej: Santiago"
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Teléfono de contacto *"
                value={formDireccion.telefono}
                onChange={(e) => {
                  let value = e.target.value;
                  // Si está vacío o solo tiene +56, establecer +56 9
                  if (value === '' || value === '+') {
                    value = '+56 9';
                  }
                  // Si no empieza con +56, agregarlo
                  if (!value.startsWith('+56')) {
                    value = '+56 9' + value.replace(/^\+?56\s?9?\s?/, '');
                  }
                  // Solo permitir números, espacios y el prefijo +56
                  if (/^\+56\s?9?[0-9\s]*$/.test(value)) {
                    setFormDireccion({ ...formDireccion, telefono: value });
                  }
                }}
                onFocus={(e) => {
                  // Si está vacío al hacer foco, poner +56 9
                  if (!e.target.value || e.target.value === '') {
                    setFormDireccion({ ...formDireccion, telefono: '+56 9' });
                  }
                }}
                placeholder="+56 9 1234 5678"
                helperText="Formato automático: +56 9 XXXX XXXX"
                inputProps={{ maxLength: 16 }}
                sx={{
                  ...textFieldStyles,
                  '& .MuiFormHelperText-root': {
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Referencia o instrucciones de entrega"
                value={formDireccion.referencia}
                onChange={(e) => setFormDireccion({ ...formDireccion, referencia: e.target.value })}
                placeholder="Edificio color beige, portón negro, etc."
                multiline
                rows={2}
                sx={textFieldStyles}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{
          px: { xs: 2.5, sm: 3.5 },
          py: { xs: 2, sm: 2.5 },
          backgroundColor: '#fafbfc',
          gap: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          borderTop: '1px solid #e8e8e8'
        }}>
          <Button
            onClick={handleCloseDialogDireccion}
            fullWidth={true}
            sx={{
              borderRadius: { xs: '12px', sm: '12px' },
              px: { xs: 3, sm: 4 },
              py: { xs: 1.2, sm: 1.3 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 600,
              color: '#555',
              backgroundColor: '#f0f0f0',
              border: '1.5px solid #e0e0e0',
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '140px' },
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#e8e8e8',
                borderColor: '#d0d0d0',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveDireccion}
            variant="contained"
            disabled={savingAddress}
            fullWidth={true}
            sx={{
              borderRadius: { xs: '12px', sm: '12px' },
              px: { xs: 3, sm: 4 },
              py: { xs: 1.2, sm: 1.3 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 600,
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '140px' },
              background: 'linear-gradient(135deg, #2e5a33 0%, #3d7547 50%, #4a8c5a 100%)',
              boxShadow: '0 4px 14px rgba(46, 90, 51, 0.35)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a3d1e 0%, #2e5a33 50%, #3d7547 100%)',
                boxShadow: '0 6px 20px rgba(46, 90, 51, 0.45)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0px)',
                boxShadow: '0 2px 8px rgba(46, 90, 51, 0.3)',
              }
            }}
          >
            {savingAddress ? 'Guardando...' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* COMENTADO: Dialog para agregar tarjeta eliminado - Solo pagos por WhatsApp */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </CheckoutContainer>
  );
};

export default Checkout;